// ============================================================================
// L'INFOBULLE ANCRÉE — l'explication d'un contrôle, dans la peau de celle du tableau de bord.
//
// ⚠️ POURQUOI ELLE EXISTE : `title` du navigateur n'est ni stylable, ni patient (il s'efface au bout de
// quelques secondes, souvent avant la fin d'une phrase), et il est IGNORÉ sur un élément `disabled` par
// une partie des navigateurs. Or c'est précisément là qu'on en a le plus besoin — un champ inerte sans
// raison affichée fait chercher une panne là où il y a une règle (§23.0).
//
// ⚠️ ELLE ENVELOPPE SON DÉCLENCHEUR au lieu de s'y attacher par `ref` : un champ désactivé n'émet ni
// `mouseenter` ni `focus`. C'est l'enveloppe qui écoute, et c'est la seule façon d'expliquer un contrôle
// que le navigateur a rendu sourd.
//
// ⚠️ EN PORTAIL ET EN POSITION FIXE : les tuiles qui l'appellent vivent dans des grilles défilantes et
// des modales bornées. Rendue en flux, elle serait rognée par le premier ancêtre en `overflow`.
// ============================================================================
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { anchorTip, TIP_SKIN, TOOLTIP, type TipPlacement } from '@orgazone/ui-tokens';
// ⚠️ La feuille injectée par ce module porte la règle qui rend l'infobulle possible sur un contrôle
// DÉSACTIVÉ — c'est-à-dire dans le cas même pour lequel ce composant remplace l'attribut `title`.
import { OZ_TIP_ANCHOR } from './interactive';

export interface TooltipProps {
  /** L'explication. Absente : l'enveloppe rend ses enfants et rien de plus. */
  content?: ReactNode;
  /** Surtitre facultatif, en capitales — il NOMME ce que l'explication justifie (« code verrouillé »). */
  label?: string;
  children: ReactNode;
  /** `inline-flex` par défaut ; `block` quand l'enveloppe doit occuper la largeur de son parent. */
  display?: 'inline-flex' | 'block';
  style?: CSSProperties;
}

/** Hors de l'écran le temps d'être mesurée — jamais visible à cette position. */
const AVANT_MESURE: TipPlacement = { left: -9999, top: -9999, placement: 'above', arrowX: 0 };

/**
 * Explique un élément au survol et au focus, sans jamais lui voler le pointeur.
 *
 * ⚠️ SURVOL **ET** FOCUS : au clavier, il n'y a pas de survol. Une explication qui n'existe qu'à la
 * souris n'existe pas pour qui navigue au clavier.
 * ⚠️ `aria-describedby` PLUTÔT QU'UN TEXTE VISUEL SEUL : le lecteur d'écran annonce l'explication avec
 * le contrôle. C'est ce que `title` faisait gratuitement — le remplacer sans le rendre, ce serait
 * troquer de l'information contre de l'esthétique.
 */
export function Tooltip({ content, label, children, display = 'inline-flex', style }: TooltipProps) {
  const [ouvert, setOuvert] = useState(false);
  const [pose, setPose] = useState<TipPlacement>(AVANT_MESURE);
  const ancre = useRef<HTMLSpanElement | null>(null);
  const bulle = useRef<HTMLDivElement | null>(null);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  const close = useCallback(() => {
    if (minuteur.current) { clearTimeout(minuteur.current); minuteur.current = null; }
    setOuvert(false);
    setPose(AVANT_MESURE);
  }, []);

  // ⚠️ LE DÉLAI EST À L'OUVERTURE SEULEMENT : la grille des déclinaisons compte des dizaines de tuiles
  // voisines, et la traverser à la souris ferait clignoter autant de bulles. La fermeture, elle, est
  // immédiate — une bulle qui s'attarde masque ce qu'on voulait lire ensuite.
  const open = useCallback((immediat: boolean) => {
    if (minuteur.current) clearTimeout(minuteur.current);
    if (immediat) { setOuvert(true); return; }
    minuteur.current = setTimeout(() => setOuvert(true), TOOLTIP.openDelay);
  }, []);

  useEffect(() => () => { if (minuteur.current) clearTimeout(minuteur.current); }, []);

  // ⚠️ MESURÉE PUIS POSÉE, en `useLayoutEffect` : la largeur dépend du texte, donc le placement ne peut
  // pas être calculé avant le rendu. Ce hook s'exécute AVANT la peinture — la position hors écran de la
  // première passe n'est jamais visible.
  useLayoutEffect(() => {
    if (!ouvert || !ancre.current || !bulle.current) return;
    const t = ancre.current.getBoundingClientRect();
    setPose(anchorTip(
      { left: t.left, right: t.right, top: t.top, bottom: t.bottom },
      { width: bulle.current.offsetWidth, height: bulle.current.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight },
      { gap: TOOLTIP.gap, edgeGap: TOOLTIP.edgeGap, arrowHalf: TOOLTIP.arrowHalf, radiusPx: TOOLTIP.radiusPx },
    ));
  }, [ouvert, content, label]);

  // ⚠️ LE DÉFILEMENT FERME. Une bulle en position fixe ne suit pas la page : elle resterait posée sur un
  // élément qui a bougé, et désignerait alors son voisin.
  useEffect(() => {
    if (!ouvert) return;
    const surSortie = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', surSortie);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', surSortie);
    };
  }, [ouvert, close]);

  if (!content) return <>{children}</>;

  const dessus = pose.placement === 'above';
  return (
    <span
      ref={ancre}
      className={OZ_TIP_ANCHOR}
      onMouseEnter={() => open(false)}
      onMouseLeave={close}
      onFocusCapture={() => open(true)}
      onBlurCapture={close}
      aria-describedby={ouvert ? id : undefined}
      style={{ display, ...(display === 'inline-flex' ? { alignItems: 'center' } : null), ...style }}
    >
      {children}
      {ouvert
        ? createPortal(
            <div
              ref={bulle}
              id={id}
              role="tooltip"
              style={{
                position: 'fixed', left: pose.left, top: pose.top,
                // ⚠️ TRANSPARENTE AU POINTEUR : posée entre le curseur et sa cible, une bulle qui capte
                // les événements provoque un `mouseleave` sur son propre déclencheur — elle se ferme,
                // donc se rouvre, et clignote sans fin.
                pointerEvents: 'none', zIndex: TOOLTIP.z,
                maxWidth: TOOLTIP.maxWidth,
                padding: TIP_SKIN.padV + 'px ' + TIP_SKIN.padH + 'px',
                borderRadius: 'var(--r-' + TIP_SKIN.radius + ')',
                // ⚠️ OMBRE PAR RÔLE (`--sh-popover`) et non en noir littéral : elle se dérive de l'encre
                // du thème courant, donc elle reste juste en sombre — un noir figé y ferait un halo.
                boxShadow: 'var(--sh-popover)',
                background: 'var(--ink-900)', color: 'var(--paper)',
                fontSize: TOOLTIP.textSize, lineHeight: TOOLTIP.lineHeight,
              }}
            >
              {label ? (
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: TIP_SKIN.labelSize,
                  letterSpacing: TIP_SKIN.labelTracking, textTransform: 'uppercase',
                  opacity: TIP_SKIN.labelOpacity, marginBottom: 2,
                }}>{label}</div>
              ) : null}
              {content}
              {/* La pointe DIT DE QUI la bulle parle : dans une grille dense, une bulle flottante
                  pourrait décrire n'importe laquelle des tuiles voisines. */}
              <span
                aria-hidden
                style={{
                  position: 'absolute', left: pose.arrowX - TOOLTIP.arrowHalf,
                  [dessus ? 'bottom' : 'top']: -TOOLTIP.arrowRise,
                  width: TOOLTIP.arrowHalf * 2, height: TOOLTIP.arrowRise,
                  background: 'var(--ink-900)',
                  clipPath: dessus ? 'polygon(0 0, 100% 0, 50% 100%)' : 'polygon(50% 0, 0 100%, 100% 100%)',
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
